import { Bot, Check, MessageSquarePlus, X } from "lucide-react"
import { useId, useState } from "react"

type BrowserUserActionBannerProps = {
    instruction: string
    status: "waiting" | "delivering" | "delivery-failed"
    outcome?: "completed" | "unable"
    error?: string
    onComplete: (completed: boolean, userNotes: string) => void
}

export function BrowserUserActionBanner({ instruction, status, outcome, error, onComplete }: BrowserUserActionBannerProps): JSX.Element {
    const notesId = useId()
    const [userNotes, setUserNotes] = useState("")
    const [notesExpanded, setNotesExpanded] = useState(false)
    const deliveryLocked = status === "delivering" || outcome !== undefined

    return (
        <div className="shrink-0 border-b border-warning/40 bg-warning/10 px-2 py-1.5" role="region" aria-label="Agent needs your help">
            <div className="flex min-w-0 items-center gap-2">
                <Bot size={15} className="shrink-0 text-warning" />
                <p
                    className="max-h-10 min-w-0 flex-1 overflow-y-auto whitespace-pre-wrap break-words text-xs leading-4 text-base-content"
                    tabIndex={0}
                    aria-label={`Requested Browser step: ${instruction}`}
                >
                    <span className="font-semibold">Your step:</span> {instruction}
                </p>
                <div className="flex shrink-0 items-center gap-1">
                    <button
                        type="button"
                        className="btn flex h-7 items-center gap-1 border border-border bg-base-100 px-2 text-[11px] font-medium text-muted hover:text-base-content disabled:opacity-60"
                        aria-label={notesExpanded ? "Hide the note for the agent" : "Add a note for the agent"}
                        aria-expanded={notesExpanded}
                        title={notesExpanded ? "Hide note" : "Add note"}
                        disabled={deliveryLocked}
                        onClick={() => setNotesExpanded((expanded) => !expanded)}
                    >
                        <MessageSquarePlus size={12} />
                        Note
                    </button>
                    {outcome ? (
                        <button
                            type="button"
                            className="btn h-7 bg-primary px-2.5 text-[11px] font-medium text-primary-content hover:bg-primary/90 disabled:cursor-wait disabled:opacity-60"
                            aria-label="Retry delivery"
                            disabled={status === "delivering"}
                            onClick={() => onComplete(outcome === "completed", userNotes)}
                        >
                            {status === "delivering" ? "Starting…" : "Retry"}
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                className="btn flex h-7 items-center gap-1 border border-border bg-base-100 px-2 text-[11px] font-medium text-muted hover:text-base-content"
                                aria-label="I can’t do this"
                                title="I can’t do this"
                                disabled={status === "delivering"}
                                onClick={() => onComplete(false, userNotes)}
                            >
                                <X size={12} />
                                Can’t
                            </button>
                            <button
                                type="button"
                                className="btn flex h-7 items-center gap-1 bg-primary px-2 text-[11px] font-medium text-primary-content hover:bg-primary/90 disabled:cursor-wait disabled:opacity-60"
                                aria-label="Done — start next turn"
                                title="Done — start next turn"
                                disabled={status === "delivering"}
                                onClick={() => onComplete(true, userNotes)}
                            >
                                <Check size={12} />
                                {status === "delivering" ? "Starting…" : "Done"}
                            </button>
                        </>
                    )}
                </div>
            </div>
            {notesExpanded && (
                <div className="ml-6 mt-1.5">
                    <label className="sr-only" htmlFor={notesId}>
                        Add a note for the agent (optional)
                    </label>
                    <textarea
                        id={notesId}
                        className="min-h-8 max-h-24 w-full resize-y border border-border bg-base-100 px-2 py-1.5 text-xs leading-4 text-base-content outline-none placeholder:text-muted/70 focus:border-primary"
                        value={userNotes}
                        maxLength={2_000}
                        rows={1}
                        disabled={deliveryLocked}
                        placeholder="Optional note for the agent"
                        onChange={(event) => setUserNotes(event.target.value)}
                    />
                </div>
            )}
            {status === "delivery-failed" && (
                <p className="ml-6 mt-1.5 text-xs leading-4 text-error" role="alert">
                    {error ?? "The result could not be delivered. It is still pending."}
                </p>
            )}
        </div>
    )
}
